/**
 * CAP-TAX-1 · CapabilityDisabledError
 *
 * Dual export — one Error class, one React component, both under the name
 * CapabilityDisabledError:
 *   • CapabilityDisabledError (Error)  — thrown from requireCapability()
 *   • CapabilityDisabled       (React) — default export; renders the
 *                                        coherent empty state per KPAP-1
 *                                        Rule 2 ("disabled capabilities
 *                                        render gracefully").
 *
 * All styling uses watchmen-theme.css CSS variables. No hardcoded hex.
 */
import React from 'react';
import type { CapabilityFlag } from './capabilities';

export class CapabilityDisabledError extends Error {
  readonly flag: CapabilityFlag;
  readonly tenantId: string;
  readonly reason?: string;

  constructor(tenantId: string, flag: CapabilityFlag, reason?: string) {
    super(
      reason ??
        `Capability "${flag}" is not enabled for tenant "${tenantId}".`,
    );
    this.name = 'CapabilityDisabledError';
    this.flag = flag;
    this.tenantId = tenantId;
    this.reason = reason;
  }
}

export interface CapabilityDisabledProps {
  flag: CapabilityFlag;
  reason?: string;
  contactAdmin?: boolean;
}

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--sp-4)',
  padding: 'var(--sp-12) var(--sp-6)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-1)',
  color: 'var(--text-primary)',
  textAlign: 'center',
  minHeight: '320px',
};

const headlineStyle: React.CSSProperties = {
  fontSize: 'var(--font-2xl)',
  fontWeight: 500,
  letterSpacing: '0.04em',
  color: 'var(--kairos-navy-on-dark)',
  margin: 0,
};

const accentStyle: React.CSSProperties = {
  width: '48px',
  height: '2px',
  background: 'var(--kairos-gold)',
  borderRadius: 'var(--radius-pill)',
};

const bodyStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--font-md)',
  color: 'var(--text-secondary)',
  maxWidth: '480px',
  margin: 0,
  lineHeight: 1.5,
};

const flagStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 'var(--font-sm)',
  color: 'var(--accent)',
  background: 'var(--accent-bg)',
  border: '1px solid var(--accent-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '2px 8px',
};

const adminHintStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--font-sm)',
  color: 'var(--text-tertiary)',
  margin: 0,
};

export function CapabilityDisabled({
  flag,
  reason,
  contactAdmin = true,
}: CapabilityDisabledProps): React.ReactElement {
  return (
    <section
      role="status"
      aria-live="polite"
      data-capability-flag={flag}
      style={containerStyle}
    >
      <h2 style={headlineStyle}>This capability is not enabled</h2>
      <span style={accentStyle} aria-hidden="true" />
      <p style={bodyStyle}>
        {reason ??
          'Your tenant is not licensed for this feature on the current plan.'}
      </p>
      <span style={flagStyle}>{flag}</span>
      {contactAdmin && (
        <p style={adminHintStyle}>
          Contact your Kairos administrator to enable it.
        </p>
      )}
    </section>
  );
}

export default CapabilityDisabled;
